import React from 'react';
import { Plus, X } from 'lucide-react';
import { SIMPLE_TRANSFORMS, PARAM_TRANSFORMS } from './constants';

// Edits the `transforms` array for a single mapping field. Transforms are
// stored on the wire as either a string (simple) or a single-key object
// (parameterized). This component normalizes them into a friendly shape for
// editing and serializes back on every change.

const ALL_TYPES = [...SIMPLE_TRANSFORMS, ...PARAM_TRANSFORMS];

const LABELS = {
  lowercase: 'Lowercase',
  uppercase: 'Uppercase',
  arrayWrap: 'Wrap in array',
  dateFormat: 'Format date',
  prefix: 'Add prefix',
  suffix: 'Add suffix',
  concat: 'Join fields',
  booleanMap: 'Map true/false',
  constant: 'Fixed value',
  default: 'Fallback value',
};

const isSimple = (type) => SIMPLE_TRANSFORMS.includes(type);

// wire form -> editable {type, ...params}
const decode = (t) => {
  if (typeof t === 'string') return { type: t };
  const key = Object.keys(t)[0];
  const arg = t[key];
  if (key === 'booleanMap') {
    return { type: key, trueVal: arg?.true ?? '', falseVal: arg?.false ?? '' };
  }
  if (key === 'concat') {
    return { type: key, fields: (arg?.fields ?? []).join(', '), sep: arg?.sep ?? ' ' };
  }
  return { type: key, value: arg };
};

// editable -> wire form
const encode = (t) => {
  if (isSimple(t.type)) return t.type;
  if (t.type === 'booleanMap') {
    return { booleanMap: { true: t.trueVal ?? '', false: t.falseVal ?? '' } };
  }
  if (t.type === 'concat') {
    return {
      concat: {
        fields: String(t.fields ?? '')
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
        sep: t.sep ?? ' ',
      },
    };
  }
  return { [t.type]: t.value ?? '' };
};

const TransformsEditor = ({ transforms, onChange }) => {
  const list = (transforms ?? []).map(decode);

  const update = (next) => onChange(next.map(encode));
  const addTransform = () => update([...list, { type: 'lowercase' }]);
  const removeTransform = (i) => update(list.filter((_, idx) => idx !== i));
  const patch = (i, p) => update(list.map((t, idx) => (idx === i ? { ...t, ...p } : t)));

  return (
    <div className="tw-space-y-1.5">
      {list.map((t, i) => (
        <div key={i} className="tw-flex tw-items-center tw-gap-1.5 tw-flex-wrap">
          <span className="tw-text-gray-300 tw-text-xs">↳</span>
          <select
            value={t.type}
            onChange={(e) => patch(i, { type: e.target.value })}
            className="tw-px-2 tw-py-1 tw-text-xs tw-border tw-border-gray-200 tw-rounded-md tw-bg-white"
          >
            {ALL_TYPES.map((ty) => (
              <option key={ty} value={ty}>
                {LABELS[ty] ?? ty}
              </option>
            ))}
          </select>
          <ParamInputs t={t} onPatch={(p) => patch(i, p)} />
          <button
            type="button"
            onClick={() => removeTransform(i)}
            className="tw-p-1 tw-text-gray-400 hover:tw-text-red-500 tw-bg-transparent tw-border-0 tw-cursor-pointer"
            aria-label="Remove transform"
          >
            <X className="tw-w-3.5 tw-h-3.5" />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={addTransform}
        className="tw-flex tw-items-center tw-gap-1 tw-px-2 tw-py-1 tw-text-xs tw-text-indigo-600 tw-bg-transparent tw-border-0 tw-cursor-pointer hover:tw-text-indigo-800"
      >
        <Plus className="tw-w-3 tw-h-3" /> Add transform
      </button>
    </div>
  );
};

const ParamInputs = ({ t, onPatch }) => {
  if (isSimple(t.type)) return null;

  if (t.type === 'booleanMap') {
    return (
      <span className="tw-flex tw-items-center tw-gap-1">
        <SmallInput placeholder="if true" value={t.trueVal} onChange={(v) => onPatch({ trueVal: v })} />
        <SmallInput placeholder="if false" value={t.falseVal} onChange={(v) => onPatch({ falseVal: v })} />
      </span>
    );
  }
  if (t.type === 'concat') {
    return (
      <span className="tw-flex tw-items-center tw-gap-1">
        <SmallInput placeholder="fieldA, fieldB" value={t.fields} onChange={(v) => onPatch({ fields: v })} wide />
        <SmallInput placeholder="separator" value={t.sep} onChange={(v) => onPatch({ sep: v })} />
      </span>
    );
  }
  const ph = {
    dateFormat: 'DD-MM-YYYY',
    prefix: 'prefix text',
    suffix: 'suffix text',
    constant: 'fixed value',
    default: 'fallback value',
  }[t.type];
  return <SmallInput placeholder={ph} value={t.value} onChange={(v) => onPatch({ value: v })} wide />;
};

const SmallInput = ({ value, onChange, placeholder, wide }) => (
  <input
    type="text"
    value={value ?? ''}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    className={[
      'tw-px-2 tw-py-1 tw-text-xs tw-border tw-border-gray-200 tw-rounded-md focus:tw-border-indigo-500 focus:tw-outline-none',
      wide ? 'tw-w-36' : 'tw-w-24',
    ].join(' ')}
  />
);

export default TransformsEditor;
